import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { fetchWebsitesByIds } from "../../redux/reducer/turkishWebsitesMainReducer";
import CustomBrandsCard from "./customBrandsCard";
import CustomLines from "../home/customLines";
import CustomBigText from "../sharedComponents/customBigText";
import { useTranslation } from "react-i18next";
import CustomLogosCard from "./customLogosCard";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { ChevronDoubleDownIcon, InboxIcon } from "@heroicons/react/24/outline";

import {
  ChevronDoubleRightIcon,
  ChevronDoubleLeftIcon,
} from "@heroicons/react/24/outline";
import LoadingSpinner from "../animation/LoadingSpinner";
import useLanguageDirection from "../../hooks/useLanguageDirection";
import { fetchCategoryBySlug } from "../../redux/reducer/categoryReducer";

import CopyLinkWithoutBackgroundV2 from "../iFrame/copyLinkWithoutBackgroundV2";
import { useLanguageNavigate } from "../../hooks/useLanguageNavigation";
import CustomButton from "../sharedComponents/customButton";

const ShoppingBrandsWebsites = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [categories, setCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoriesIds, setCategoriesIds] = useState({});
  const [isPaused, setIsPaused] = useState(false);
  const resumeTimeout = useRef(null);
  const sectionRef = useRef(null);
  const sliderRef = useRef(null);

  const [visibleCount, setVisibleCount] = useState(12);
  const websites = categories[selectedCategory]?.data?.brand || [];
  const visibleWebsites = websites.slice(0, visibleCount);
  const direction = useLanguageDirection();
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const navigateWithLang = useLanguageNavigate();

  const getSlidesToShow = () => {
    if (window.innerWidth >= 1280) return 6;
    if (window.innerWidth >= 1024) return 5;
    if (window.innerWidth >= 768) return 4;
    if (window.innerWidth >= 640) return 3;
    return 2;
  };

  const [slidesToShow, setSlidesToShow] = useState(getSlidesToShow());

  useEffect(() => {
    const handleResize = () => {
      setSlidesToShow(getSlidesToShow());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const handlePause = () => {
    clearTimeout(resumeTimeout.current);
    setIsPaused(true);
    sliderRef.current?.slickPause();
  };
  useEffect(() => {
    return () => clearTimeout(resumeTimeout.current);
  }, []);
  const handleResume = () => {
    clearTimeout(resumeTimeout.current);
    resumeTimeout.current = setTimeout(() => {
      setIsPaused(false);
      sliderRef.current?.slickPlay();
    }, 3000);
  };
  const categoriesList = Object.keys(categories);
  const shouldUseCarousel = categoriesList.length > slidesToShow;

  // Custom arrow components
  const CustomPrevArrow = (props) => {
    const { style, onClick } = props;
    return (
      <button
        className="absolute z-10 p-2 -translate-y-1/2 bg-white duration-300 hover:bg-custom-yellow top-1/2 rounded-full shadow-md left-[0px] group hidden md:block"
        style={{ ...style }}
        onClick={onClick}
      >
        <ChevronDoubleLeftIcon className="text-black duration-300 w-7 h-7 group-hover:text-white" />
      </button>
    );
  };

  const CustomNextArrow = (props) => {
    const { style, onClick } = props;
    return (
      <button
        className="absolute z-10 p-2 -translate-y-1/2 bg-white duration-300 hover:bg-custom-yellow top-1/2 rounded-full shadow-md right-[0px] group hidden md:block"
        style={{ ...style }}
        onClick={onClick}
      >
        <ChevronDoubleRightIcon className="text-black duration-300 h-7 w-7 group-hover:text-white" />
      </button>
    );
  };
  const handleBuyForMe = () => {
    navigateWithLang("/explain-buy", { replace: false });
    window.location.reload();
  };
  // Slick settings
  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: slidesToShow,
    slidesToScroll: 2,
    autoplay: true,
    autoplaySpeed: 2500,
    pauseOnHover: true,
    pauseOnFocus: true,
    rtl: direction === "rtl",
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    responsive: [
      { breakpoint: 1280, settings: { slidesToShow: 5 } },
      { breakpoint: 1024, settings: { slidesToShow: 4 } },
      { breakpoint: 768, settings: { slidesToShow: 3, arrows: false } },
      { breakpoint: 640, settings: { slidesToShow: 2, arrows: false } },
    ],
    onSwipe: () => {
      setIsUserInteracting(true);
      sliderRef.current?.slickPause();
    },
    beforeChange: () => {
      if (isUserInteracting) {
        setTimeout(() => {
          sliderRef.current?.slickPlay();
          setIsUserInteracting(false);
        }, 2000);
      }
    },
  };

  useEffect(() => {
    const ids = Array.isArray(location.state?.websites)
      ? location.state.websites
      : [];

    const loadFromWebsites = async (ids) => {
      try {
        const result = await dispatch(fetchWebsitesByIds(ids));
        const validData = result.payload || [];

        if (validData.length) {
          const groupedCategories = validData.reduce((acc, website, index) => {
            const category =
              website.category?.ar || website.category?.en || "Uncategorized";
            if (!acc[category]) acc[category] = [];
            acc[category].push({
              ...website,
              id: website.id || website._id || index,
            });
            return acc;
          }, {});

          setCategories(groupedCategories.Uncategorized || []);
          if (Object.keys(groupedCategories).length > 0 && !selectedCategory) {
            setSelectedCategory(Object.keys(groupedCategories)[0]);
          }
        } else {
          setError("No valid websites found.");
        }
      } catch (error) {
        setError("Error fetching websites: " + error.message);
      } finally {
        setLoading(false);
      }
    };

    const loadFromSlug = async () => {
      try {
        const pathSegments = location.pathname.split("/").filter(Boolean);
        const slug_subcategory = pathSegments[pathSegments.length - 1];
        const slug_category = pathSegments[pathSegments.length - 2];

        let area = "";
        if (slug_category === "category-europe") {
          area = "Europe";
        } else if (slug_category === "category-turkey") {
          area = "Turkey";
        }

        const slug = `?slug=${slug_subcategory}&area=${area}`;
        const result = await dispatch(fetchCategoryBySlug(slug));
        const categoryData = result.payload;

        if (!categoryData) {
          setError("No subcategories found.");
          setLoading(false);
          return;
        }

        const allSubCategoryIds =
          categoryData.subCategory?.map((sub) => sub._id) || [];
        setCategoriesIds(allSubCategoryIds);
        await loadFromWebsites(allSubCategoryIds);
      } catch (error) {
        setError("Error fetching category by slug.");
        setLoading(false);
      }
    };

    if (ids.length > 0) {
      loadFromWebsites(ids);
    } else {
      loadFromSlug();
    }
  }, [dispatch, location.state?.websites, location.pathname]);

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 200);
  };

  if (loading) return <LoadingSpinner />;

  if (error)
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-xl font-semibold text-red-600">{error}</p>
      </div>
    );

  if (!Object.keys(categories).length)
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-xl font-semibold text-gray-600">{t("NoWebsites")}</p>
      </div>
    );

  return (
    <div dir={direction}>
      <div className="flex flex-col items-center justify-center pb-5 text-center max-container">
        <CustomLines />
        <CustomBigText title={t("shopping_choices_title")} />
        <div className="flex flex-col items-center justify-center pb-5 my-4 md:pb-0 md:my-8">
          <p className="text-sm font-medium text-center text-gray-700 ">
            {t("shopping_experience")}{" "}
          </p>
          <ChevronDoubleDownIcon className="w-6 h-6 my-3 text-gray-700" />
          <CustomButton
            children={t("servicesBuySection.buttonLabel")}
            icon={InboxIcon}
            onClick={handleBuyForMe}
          />
        </div>

        <div
          className="relative w-full mb-5 md:mb-10"
          onMouseEnter={handlePause}
          onMouseLeave={handleResume}
          onFocus={handlePause}
          onBlur={handleResume}
          onTouchStart={handlePause}
          onTouchEnd={handleResume}
          onTouchCancel={handleResume}
        >
          {shouldUseCarousel ? (
            <Slider ref={sliderRef} {...sliderSettings}>
              {categoriesList.map((category, index) => {
                const image = categories[category].data.img;
                const title =
                  i18n.language === "ar"
                    ? categories[category].data.name.ar || "فئة غير معروفة"
                    : categories[category].data.name.en || "Unknown Category";

                return (
                  <div key={`${category}-${index}`} className="px-2">
                    <div className="flex-shrink-0 min-w-[175px] max-w-[175px] lg:min-w-[200px] lg:max-w-[200px] mx-auto h-[250px] cursor-pointer">
                      <CustomBrandsCard
                        title={title}
                        image={image}
                        onCardClick={() => handleCategoryClick(category)}
                      />
                    </div>
                  </div>
                );
              })}
            </Slider>
          ) : (
            <div className="flex flex-wrap justify-center gap-4">
              {categoriesList.map((category, index) => {
                const image = categories[category].data.img;
                const title =
                  i18n.language === "ar"
                    ? categories[category].data.name.ar || "فئة غير معروفة"
                    : categories[category].data.name.en || "Unknown Category";

                return (
                  <div key={`${category}-${index}`} className="">
                    <div className="flex-shrink-0 min-w-[175px] max-w-[175px] lg:min-w-[200px] lg:max-w-[200px] mx-auto h-[250px] cursor-pointer">
                      <CustomBrandsCard
                        title={title}
                        image={image}
                        onCardClick={() => handleCategoryClick(category)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {selectedCategory !== "Uncategorized" && visibleWebsites && (
          <>
            <div
              className="flex flex-col w-full mt-10 md:mt-20"
              ref={sectionRef}
            >
              <div className="flex items-center w-full">
                <div className="bg-black h-[2px] flex-1"></div>
                <CustomBigText
                  title={t("BrandTitle")}
                  className="mx-4 text-black"
                />
                <div className="bg-black h-[2px] flex-1"></div>
              </div>

              {visibleWebsites.length > 0 ? (
                <div className="grid items-stretch grid-cols-2 gap-3 px-3 mt-5 gap-y-7 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                  {visibleWebsites.map((website, index) => {
                    const title =
                      i18n.language === "ar"
                        ? website.name?.ar
                        : website.name?.en || "Unnamed Website";
                    return (
                      <CustomLogosCard
                        key={website.id || website._id || index}
                        title={title}
                        image={website.img}
                        onCardClick={() => {
                          if (website?.link) {
                            window.open(website.link, "_blank");
                          } else {
                            console.error("Website link is unavailable.");
                            alert(t("CannotNavigateWebsite"));
                          }
                        }}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="mt-10 text-black md:mt-20" dir={direction}>
                  {t("NoWebsites")}
                </div>
              )}

              {websites.length > visibleCount && (
                <div className="flex justify-center mt-6">
                  <button
                    onClick={() => setVisibleCount((prev) => prev + 6)}
                    className="px-4 py-2 text-black underline transition rounded-md"
                  >
                    {t("ShowMore")}
                  </button>
                </div>
              )}
            </div>

            <div className="w-full mt-10 md:mt-20 ">
              <div className="flex items-center w-full mb-6">
                <div className="bg-black h-[2px] flex-1"></div>
                <CustomBigText
                  title={t("AddProductsCart")}
                  className="mx-4 text-black"
                />
                <div className="bg-black h-[2px] flex-1"></div>
              </div>
              <CopyLinkWithoutBackgroundV2 />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ShoppingBrandsWebsites;
